<template>
  <div id="graph-container">
    <svg :viewBox="`0 0 ${graphWidth} ${graphHeight}`">
      <g ref="bars">
        <rect class="series-bar"></rect>
      </g>

      <g ref="barLabels">
        <text class="series-bar-label"></text>
      </g>

      <g ref="xaxis"></g>
      <g ref="yaxis"></g>
    </svg>
  </div>
</template>

<script>
import { FixedSeries, TimeSeries, DateDatum } from '@/js/model/DataSeries';
import { makeArray } from '@/js/model/Util';
import { formatGenericNumber } from '@/js/d3/Formats';

const d3 = require('d3');


export default {
  name: 'BarGraph',
  props: {
    datas: { type: Array },
    labels: { default: '' },
    graphHeight: {type: Number, default: 250},
    graphWidth: {type: Number, default: 600},
    margin: {
      type: Object,
      default: () => ({'top': 20, 'right': 20, 'bottom': 30, 'left': 50})
    }
  },
  mounted() {
    d3.select('#graph-container')
        .style('max-width', `${this.graphWidth}px`)
        .style('max-height', `${this.graphHeight}px`);
    this.refreshData();
    this.refreshAxes();
  },
  watch: {
    datas: function() {
      this.refreshData();
      this.refreshAxes();
    }
  },
  methods: {
    refreshAxes() {
      let xAxis = g => g
        .attr('transform', `translate(0, ${this.xAxisLocation})`)
        .call(d3.axisBottom(this.xScale));

      let yAxis = g => g
        .attr('transform', `translate(${this.margin.left}, 0)`)
        .call(d3.axisLeft(this.yScale).tickFormat(formatGenericNumber));
      
      d3.select(this.$refs.yaxis).attr("class", "axis").call(yAxis);
      d3.select(this.$refs.xaxis).attr("class", "axis").call(xAxis);
    },
    refreshData() {
      d3.select(this.$refs.bars)
        .selectAll('rect')
        .data(this.datasArray)
        .join('rect')
          .attr('class', 'series-bar')
          .attr('x', (d,i) => this.xScale(this.labelsArray[i]))
          .attr('y', d => this.yScale(d[0].value))
          .attr('height', d => this.yScale(0) - this.yScale(d[0].value))
          .attr('width', this.xScale.bandwidth());

      d3.select(this.$refs.barLabels)
        .selectAll('text')
        .data(this.datasArray)
        .join('text')
          .attr('class', 'series-bar-text')
          .attr('text-anchor', 'middle')
          .text(d => d[0].value)
          .attr('x', (d,i) => this.xScale(this.labelsArray[i]) + this.xScale.bandwidth()/2)
          .attr('y', d => this.yScale(d[0].value)-10);
    },
  },
  computed: {
    datasArray() {
      return makeArray(this.datas);
    },
    labelsArray() {
      return makeArray(this.labels);
    },
    isDateSeries() {
      return this.datasArray[0][0] instanceof DateDatum;
    },
    xScale() {
      return d3.scaleBand()
        .domain(this.labelsArray)
        .rangeRound([this.margin.left, this.graphWidth - this.margin.right])
        .paddingInner(0.05)
        .paddingOuter(2 / this.labelsArray.length);
    },
    xAxisLocation() {
      let yDomain = this.yScale.domain();
      if (yDomain[1] === 0) {
        return this.margin.top;
      } else if (yDomain[0] === 0) {
        return this.graphHeight - this.margin.bottom;
      } else {
        return this.yScale(0) || 0; // Handle NaN when y axis is not loaded
      }
    },
    yScale() {
      let axisBuffer = 1.1;
      let values = this.datasArray.map(d => d.getValues()).flat(1);
      
      let yDomains = d3.zip(
        d3.extent(values),
      );

      let yDomain = [
        d3.min(yDomains[0]) * axisBuffer,
        d3.max(yDomains[1]) * axisBuffer
      ];

      if (Math.sign(yDomain[0]) === Math.sign(yDomain[1])) {
        if (yDomain[0] < 0) {
          yDomain[1] = 0;
        } else {
          yDomain[0] = 0;
        }
      }

      return d3.scaleLinear()
        .domain(yDomain)
        .range([this.graphHeight - this.margin.bottom, this.margin.top]);
    }  
  }
}
</script>


<style scoped>
#graph-container {
  margin: auto;
}

.series-bar {
  stroke: black;
  fill: #0089cd;
}
.series-bar-label {
  font-size: .9em;
}

.axis {
  font-size: .9em;
  font-weight: bold;
}

.legend {
  font-size: 13px;
}

h3 {
  margin: 40px 0 0;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}
</style>
